import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Upload,
  History,
  Search,
  Eye,
  Trash2,
  HelpCircle,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import { QuestionItem } from "../data/interfaces/QuestionBankInterfaces";
import CustomSelect from "../components/CustomSelect";
import GenerateAIQuestionModal from "../components/GenerateAIQuestionModal";
import Alert from "../../../../app/shared/components/Alert";
import Loader from "../../../../app/shared/components/Loader";
import "../styles/QuestionBankStyles.css";

const QuestionListPage: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("All");
  const [selectedModule, setSelectedModule] = useState<string>("All");
  const [modules, setModules] = useState<{ id: string; name: string }[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const limit = 10;

  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);

  // Single Delete State
  const [deleteTarget, setDeleteTarget] = useState<QuestionItem | null>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
  const [deletingSingle, setDeletingSingle] = useState<boolean>(false);

  // Bulk Selection & Delete State
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [showBulkDeleteAlert, setShowBulkDeleteAlert] =
    useState<boolean>(false);
  const [deletingBulk, setDeletingBulk] = useState<boolean>(false);

  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "warning" | "error";
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  const fetchModules = async () => {
    try {
      const res = await QuestionBankClient.listModules();
      if (res && res.modules) {
        setModules(
          res.modules.map((m: any) => ({
            id: m.module_id,
            name: m.module_name,
          })),
        );
      }
    } catch (err) {
      console.error("Error fetching modules:", err);
    }
  };

  const fetchQuestions = async (searchOverride?: string) => {
    setLoading(true);
    try {
      const activeSearch =
        searchOverride !== undefined ? searchOverride : searchQuery;
      const res = await QuestionBankClient.listQuestions({
        module_id: selectedModule === "All" ? undefined : selectedModule,
        difficulty_level:
          selectedDifficulty === "All" ? undefined : selectedDifficulty,
        search: activeSearch,
        page,
        limit,
      });
      setQuestions(res.questions || []);
      setTotalCount(res.total || 0);
      setSelectedQuestionIds([]); // Reset selection on page or filter change
    } catch (err) {
      console.error("Error fetching question bank:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [selectedModule, selectedDifficulty, page]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (val === "") {
      setPage(1);
      fetchQuestions("");
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
    fetchQuestions("");
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setPage(1);
      fetchQuestions();
    }
  };

  // Selection Checkbox Handlers
  const isAllSelected =
    questions.length > 0 && selectedQuestionIds.length === questions.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(questions.map((q) => q.question_id));
    }
  };

  const handleToggleSelect = (qId: string) => {
    if (selectedQuestionIds.includes(qId)) {
      setSelectedQuestionIds(selectedQuestionIds.filter((id) => id !== qId));
    } else {
      setSelectedQuestionIds([...selectedQuestionIds, qId]);
    }
  };

  // Single Delete Handler
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setShowDeleteAlert(false);
    setDeleteTarget(null);
    setDeletingSingle(true);
    try {
      await QuestionBankClient.deleteQuestion(
        target.question_id,
        target.module_id,
      );
      setAlertConfig({
        show: true,
        title: "Question Deleted",
        message: "Question removed successfully.",
        type: "success",
      });
      fetchQuestions();
    } catch (err) {
      console.error("Error deleting question:", err);
    } finally {
      setDeletingSingle(false);
    }
  };

  // Bulk Delete Handler (Module-based only)
  const handleBulkDeleteConfirm = async () => {
    if (selectedQuestionIds.length === 0 || selectedModule === "All") {
      setAlertConfig({
        show: true,
        title: "Module Required",
        message: "Please select a specific Module to perform bulk deletion.",
        type: "warning",
      });
      return;
    }
    setShowBulkDeleteAlert(false);
    setDeletingBulk(true);
    try {
      const res = await QuestionBankClient.bulkDeleteQuestions(
        selectedQuestionIds,
        selectedModule,
      );

      setSelectedQuestionIds([]);
      setAlertConfig({
        show: true,
        title: "Bulk Delete Complete",
        message:
          res.message ||
          `Successfully deleted ${selectedQuestionIds.length} questions from module.`,
        type: "success",
      });
      fetchQuestions();
    } catch (err: any) {
      console.error("Error bulk deleting questions:", err);
      setAlertConfig({
        show: true,
        title: "Bulk Delete Failed",
        message:
          err?.response?.data?.detail || "Failed to delete selected questions.",
        type: "error",
      });
    } finally {
      setDeletingBulk(false);
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="qb-container">
      <Loader
        isLoading={loading || deletingBulk || deletingSingle}
        message={
          deletingSingle
            ? "Deleting Question..."
            : deletingBulk
              ? "Deleting Selected Module Questions..."
              : "Loading Question Bank..."
        }
      />

      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, show: false }))}
      />

      {/* Page Header */}
      <div className="qb-header">
        <div>
          <h1 className="qb-title">
            Total Questions <span>{totalCount}</span>
          </h1>
          {/* <p className="qb-subtitle">
            Manage question modules, bulk upload spreadsheets, add single
            questions, and perform module bulk deletes.
          </p> */}
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            className="qb-btn qb-btn-primary"
            style={{
              background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
              borderColor: "#4F46E5",
              color: "#FFF",
            }}
            onClick={() => navigate("/dashboard/question-bank/ai-generate")}
          >
            <Sparkles size={16} /> Generate with AI
          </button>
          <button
            className="qb-btn qb-btn-primary"
            onClick={() => navigate("/dashboard/question-bank/add")}
          >
            <Plus size={16} /> Add Question
          </button>
        </div>
      </div>

      {/* Filter & Action Bar */}
      <div className="qb-filter-card">
        <div
          className="qb-search-box"
          style={{ maxWidth: "320px", width: "320px", flex: "none" }}
        >
          <Search size={18} className="qb-search-icon" />
          <input
            type="text"
            className="qb-search-input"
            style={{ paddingRight: searchQuery ? "36px" : "14px" }}
            placeholder="Search question description..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
          {searchQuery && (
            <X
              size={16}
              className="qb-search-clear"
              onClick={handleClearSearch}
            />
          )}
        </div>

        <div
          className="qb-filter-group"
          style={{ display: "flex", gap: "12px", alignItems: "center" }}
        >
          <CustomSelect
            options={[
              { value: "All", label: "All Modules" },
              ...modules.map((m) => ({ value: m.id, label: m.name })),
            ]}
            value={selectedModule}
            onChange={(val) => {
              setSelectedModule(val);
              setPage(1);
            }}
            style={{ width: "180px" }}
          />

          <CustomSelect
            options={[
              { value: "All", label: "All Difficulties" },
              { value: "Easy", label: "Easy" },
              { value: "Medium", label: "Medium" },
              { value: "Hard", label: "Hard" },
            ]}
            value={selectedDifficulty}
            onChange={(val) => {
              setSelectedDifficulty(val);
              setPage(1);
            }}
            style={{ width: "150px" }}
          />

          {/* Module Selection Indicator / Dynamic Bulk Delete Button */}
          {selectedModule !== "All" ? (
            selectedQuestionIds.length > 0 && (
              <button
                className="qb-btn qb-btn-danger"
                style={{ padding: "8px 16px", fontSize: "13px" }}
                onClick={() => setShowBulkDeleteAlert(true)}
              >
                <Trash2 size={16} /> Bulk Delete Module Questions (
                {selectedQuestionIds.length})
              </button>
            )
          ) : (
            <span
              style={{
                fontSize: "12px",
                color: "#64748b",
                fontStyle: "italic",
                padding: "4px 8px",
                background: "#f1f5f9",
                borderRadius: "6px",
              }}
            >
              💡 Select a Module to enable Bulk Delete
            </span>
          )}

          <button
            className="qb-btn qb-btn-outline"
            onClick={() => fetchQuestions()}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="qb-table-card">
        {questions.length === 0 ? (
          <div
            style={{
              padding: "48px 24px",
              textAlign: "center",
              color: "#64748b",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "12px",
              }}
            >
              <HelpCircle size={32} color="#94a3b8" />
            </div>
            <h3 style={{ margin: "0 0 6px 0", fontSize: "16px", fontWeight: "700", color: "#1e293b", textAlign: "center" }}>
              No questions found
            </h3>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b", textAlign: "center" }}>
              Upload an Excel question file or add a new question to populate your Question Bank.
            </p>
          </div>
        ) : (
          <div className="qb-table-scroll">
            <table className="qb-table">
              <thead>
                <tr>
                  {selectedModule !== "All" ? (
                    <th style={{ width: "40px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        style={{
                          accentColor: "#0d6e5b",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                        title="Select / Deselect All Questions in Module"
                      />
                    </th>
                  ) : (
                    <th style={{ width: "40px" }}></th>
                  )}
                  <th style={{ width: "60px", textAlign: "center" }}>Q.no</th>
                  <th>Question Description</th>
                  <th>Module</th>
                  <th>Difficulty</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q, idx) => {
                  const isSelected = selectedQuestionIds.includes(q.question_id);
                  const questionNumber = (page - 1) * limit + idx + 1;
                  return (
                    <tr
                      key={q.question_id}
                      style={{
                        background: isSelected ? "#e6f4f1" : "transparent",
                      }}
                    >
                      <td style={{ textAlign: "center" }}>
                        {selectedModule !== "All" && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(q.question_id)}
                            style={{
                              accentColor: "#0d6e5b",
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                        )}
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          width: "50px",
                        }}
                      >
                        {questionNumber}
                      </td>

                      <td style={{ maxWidth: "440px", fontWeight: "500" }}>
                        <div
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {q.question}
                        </div>
                      </td>
                      <td>{q.module_name || "General"}</td>
                      <td>
                        <span
                          className={`qb-badge qb-badge-${(q.difficulty_level || "Easy").toLowerCase()}`}
                        >
                          {q.difficulty_level || "Easy"}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "inline-flex", gap: "8px" }}>
                          <button
                            className="qb-btn qb-btn-outline"
                            style={{ padding: "6px 12px", fontSize: "12px" }}
                            onClick={() =>
                              navigate('/dashboard/question-bank/view', {
                                state: { questionId: q.question_id }
                              })
                            }
                            title="View Question Details"
                          >
                            <Eye size={14} /> View
                          </button>
                          <button
                            className="qb-btn qb-btn-outline"
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              color: "#ef4444",
                            }}
                            onClick={() => {
                              setDeleteTarget(q);
                              setShowDeleteAlert(true);
                            }}
                            title="Delete Question"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="qb-pagination">
            <div>
              Showing {Math.min((page - 1) * limit + 1, totalCount)} to{" "}
              {Math.min(page * limit, totalCount)} of {totalCount} questions
              {selectedQuestionIds.length > 0 && (
                <strong style={{ color: "#0d6e5b", marginLeft: "12px" }}>
                  ({selectedQuestionIds.length} selected)
                </strong>
              )}
            </div>
            <div className="qb-pagination-controls">
              <button
                className="qb-btn qb-btn-outline"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <button
                className="qb-btn qb-btn-outline"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Single Delete Confirmation Alert Modal */}
      {showDeleteAlert && deleteTarget && ReactDOM.createPortal(
        <div
          className="qb-modal-overlay"
          onClick={() => setShowDeleteAlert(false)}
        >
          <div
            className="qb-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "440px" }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#b91c1c",
                marginBottom: "12px",
              }}
            >
              Delete Question?
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: "20px",
              }}
            >
              Are you sure you want to delete this question? This action cannot
              be undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button
                className="qb-btn qb-btn-outline"
                onClick={() => setShowDeleteAlert(false)}
              >
                Cancel
              </button>
              <button
                className="qb-btn qb-btn-danger"
                onClick={handleDeleteConfirm}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Delete Confirmation Alert Modal */}
      {showBulkDeleteAlert && ReactDOM.createPortal(
        <div
          className="qb-modal-overlay"
          onClick={() => setShowBulkDeleteAlert(false)}
        >
          <div
            className="qb-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "460px" }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#b91c1c",
                marginBottom: "12px",
              }}
            >
              Bulk Delete Questions?
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: "20px",
              }}
            >
              Are you sure you want to permanently delete{" "}
              <strong>{selectedQuestionIds.length}</strong> selected questions?
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button
                className="qb-btn qb-btn-outline"
                onClick={() => setShowBulkDeleteAlert(false)}
              >
                Cancel
              </button>
              <button
                className="qb-btn qb-btn-danger"
                onClick={handleBulkDeleteConfirm}
                disabled={deletingBulk}
              >
                {deletingBulk
                  ? "Deleting..."
                  : `Delete ${selectedQuestionIds.length} Questions`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI Generation Modal */}
      <GenerateAIQuestionModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onSuccess={(reqId, modId) => {
          if (reqId) {
            navigate('/dashboard/question-bank/ai-review', {
              state: { requestId: reqId, moduleId: modId }
            });
          } else {
            fetchQuestions();
          }
        }}
        modules={modules.map((m) => ({ module_id: m.id, module_name: m.name }))}
        initialModuleId={selectedModule !== "All" ? selectedModule : ""}
      />
    </div>
  );
};

export default QuestionListPage;
